import { DateOnly } from '@if/core/date'
import { t } from '@if/core/i18n'
import { EPaymentFrequency, EPaymentPlan } from '@if/core/types'
import { formatCurrencyNoDecimals } from '@if/core/utils'
import * as yup from '@if/core/yup'
import { EProtectionPlanCoverage } from '@src/data/api/calculator/calculator-api'
import { DEFAULT_ERS_SCORE } from './hooks/creditRatingBands'

// The merchant-resolved amount bounds, published into the resolver context once the financing
// config has loaded. They move with the program and the merchant, so they cannot be baked into
// the schema the way a static rule would be. A bound of 0 means "not configured" — skipped.
export interface AmountBoundsContext {
  min: number
  max: number
}

// Nothing is `required()`: the form runs in `filters` mode, which validates on every change,
// so a required rule would flag fields the agent has not reached yet. `compute` decides when
// the form is complete enough to quote.
export const standaloneCalculatorSchema = yup.object({
  financingProgramId: yup.string().nullable().defined(),
  provinceIso: yup.string().nullable().defined(),
  merchantId: yup.string().nullable().defined(),
  amountRequested: yup
    .number()
    .nullable()
    .defined()
    .positive()
    .test('amount-within-bounds', function (value) {
      // An empty field is not an error — `compute` just does not quote. An absent context means
      // the bounds have not resolved yet, so nothing is claimed about the value either way.
      const ctx = this.options.context?.amountRequested as AmountBoundsContext | undefined
      if (!ctx || value == null) return true

      if (ctx.min > 0 && value < ctx.min)
        return this.createError({
          message: t('worksheetCommon.errors.amountTooLow', { min: formatCurrencyNoDecimals(ctx.min) }),
        })

      if (ctx.max > 0 && value > ctx.max)
        return this.createError({
          message: t('worksheetCommon.errors.amountTooHigh', { max: formatCurrencyNoDecimals(ctx.max) }),
        })

      return true
    }),
  paymentPlanId: yup.string().nullable().defined(),
  paymentFrequency: yup.mixed<EPaymentFrequency>().required(),
  activationDate: yup.dateOnly().nullable().defined(),
  firstPaymentDate: yup.dateOnly().nullable().defined(),
  protectionPlan: yup.mixed<EProtectionPlanCoverage>().required(),
  // The backend falls back to the prequalified maximum term, and programs that do not price
  // on ERS ignore the score entirely.
  ersScore: yup.number().nullable().defined().positive(),
  term: yup.number().nullable().defined().positive(),
})

export type StandaloneCalculatorValues = yup.InferDraft<typeof standaloneCalculatorSchema>

export const STANDALONE_CALCULATOR_DEFAULTS: StandaloneCalculatorValues = {
  financingProgramId: '',
  provinceIso: '',
  ersScore: DEFAULT_ERS_SCORE,
  merchantId: null,
  amountRequested: null,
  term: null,
  paymentPlanId: EPaymentPlan.RegularDailyInterests,
  paymentFrequency: EPaymentFrequency.Monthly,
  activationDate: DateOnly.today(),
  firstPaymentDate: null,
  protectionPlan: EProtectionPlanCoverage.None,
}
